import { tick } from 'svelte'
import {
    containOverlayFocus,
    focusOverlayElement,
    getOverlayFocusableElements
} from './overlay-focus.js'

export const AdaptiveDialogMobileBreakpoint = 768
export const AdaptiveDialogMobileBackdropTransitionMs = 180
export const AdaptiveDialogMobileSheetTransitionMs = 220

const MobileDragDismissThresholdPx = 96

type AdaptiveDialogControllerOptions = {
    dismissalBlocked: () => boolean
    focusContainerInitially?: () => boolean
    mobile: () => boolean
    onClose: () => void
    onDismissAccepted?: () => void
    onDismissRequest?: () => boolean
    returnFocusTo: () => HTMLElement | undefined
    swipeDismissEnabled: () => boolean
}

export class AdaptiveDialogController {
    public visible = $state(true)
    public dragging = $state(false)
    public dragOffsetY = $state(0)

    #closing = false
    #dragStartY = 0
    #dragPointerId: number | undefined
    #closeTimeout: ReturnType<typeof setTimeout> | undefined
    #dialogElement: HTMLElement | undefined

    public constructor(private readonly options: AdaptiveDialogControllerOptions) {}

    public get sheetTransform(): string | undefined {
        return this.dragOffsetY > 0 ? `translateY(${this.dragOffsetY}px)` : undefined
    }

    public get sheetTransition(): string {
        return this.dragging ? 'none' : 'transform 180ms cubic-bezier(0.2, 0, 0, 1)'
    }

    public close = (): void => {
        if (this.#closing || this.options.dismissalBlocked()) {
            return
        }

        this.dragging = false
        this.dragOffsetY = 0

        if (this.options.onDismissRequest?.() === false) {
            return
        }

        this.#closing = true
        this.options.onDismissAccepted?.()

        if (!this.options.swipeDismissEnabled()) {
            this.#finishClose()
            return
        }

        this.visible = false
        this.#closeTimeout = setTimeout(
            () => this.#finishClose(),
            AdaptiveDialogMobileSheetTransitionMs
        )
    }

    public handleBackdropClick = (event: MouseEvent): void => {
        if (event.target === event.currentTarget) {
            this.close()
        }
    }

    public handleKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
            this.close()
            return
        }

        containOverlayFocus(event, this.#dialogElement)
    }

    public attachDialog = (element: HTMLElement): (() => void) => {
        let attached = true
        this.#dialogElement = element

        void tick().then(() => {
            if (!attached || this.#dialogElement !== element) {
                return
            }

            if (this.options.focusContainerInitially?.()) {
                focusOverlayElement(element)
                return
            }

            const inputElement = getOverlayFocusableElements(element).find(
                (focusableElement) => focusableElement instanceof HTMLInputElement
            )
            focusOverlayElement(inputElement ?? element)
        })

        return () => {
            attached = false

            if (this.#dialogElement === element) {
                this.#dialogElement = undefined
            }
        }
    }

    public handleDragPointerDown = (event: PointerEvent): void => {
        if (!this.#canDragDismiss(event)) {
            return
        }

        this.#dragPointerId = event.pointerId
        this.#dragStartY = event.clientY
        this.dragOffsetY = 0
        this.dragging = true

        if (event.currentTarget instanceof HTMLElement) {
            event.currentTarget.setPointerCapture?.(event.pointerId)
        }
    }

    public handleDragPointerMove = (event: PointerEvent): void => {
        if (!this.dragging || event.pointerId !== this.#dragPointerId) {
            return
        }

        const nextOffset = Math.max(0, event.clientY - this.#dragStartY)
        if (nextOffset > 4) {
            event.preventDefault()
        }

        this.dragOffsetY = nextOffset
    }

    public handleDragPointerUp = (event: PointerEvent): void => {
        if (!this.dragging || event.pointerId !== this.#dragPointerId) {
            return
        }

        if (event.currentTarget instanceof HTMLElement) {
            event.currentTarget.releasePointerCapture?.(event.pointerId)
        }

        this.dragging = false
        this.#dragPointerId = undefined

        if (this.dragOffsetY >= MobileDragDismissThresholdPx) {
            this.close()
            return
        }

        this.dragOffsetY = 0
    }

    #finishClose(): void {
        if (this.#closeTimeout) {
            clearTimeout(this.#closeTimeout)
            this.#closeTimeout = undefined
        }

        focusOverlayElement(this.options.returnFocusTo())
        this.options.onClose()
    }

    #canDragDismiss(event: PointerEvent): boolean {
        if (
            !this.options.mobile() ||
            !this.options.swipeDismissEnabled() ||
            this.options.dismissalBlocked() ||
            this.#closing
        ) {
            return false
        }

        if (event.pointerType === 'mouse' && event.button !== 0) {
            return false
        }

        if (
            event.target instanceof Element &&
            event.target.closest('button, input, textarea, select, a')
        ) {
            return false
        }

        return true
    }
}
